/**
 * EMAIL EDIT FORM BODY — Form di modifica inline dei campi di una Email.
 *
 * Embed dentro AccordionDetails di ActivityTimeline. Pattern coerente
 * con CallEditFormBody ma senza status (Email non lo traccia).
 */
import { useTranslation } from 'react-i18next';
import { EditableField, type EditableValue } from '../../components/EditableField';
import { RichTextField } from '../../components/RichTextField';
import { useAppDispatch } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import { updateEmail } from './emailThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { ActivityTools } from '../../components/ActivityTools';
import { EMAIL_DIRECTIONS, type Email, type EmailCreatePayload, type EmailUpdatePayload } from './emailTypes';

interface EmailEditFormBodyProps {
  email: Email;
}

export function EmailEditFormBody({ email }: EmailEditFormBodyProps) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();

  const emailFqn = getEntityFqn('Email');
  const {
    records: currentlyLinked,
    onRecordsChange: handleRecordsChange,
    recordsSaving,
    pickerProps: recordsPickerProps,
  } = useAssociatedRecordsBinder({
    mode: 'edit',
    entityFqn: emailFqn ?? '',
    entityId: email.emailId,
  });

  const commitField =
    (field: keyof EmailCreatePayload) =>
    async (newValue: EditableValue) => {
      try {
        const payload = { emailId: email.emailId, [field]: newValue } as EmailUpdatePayload;
        await dispatch(updateEmail(payload)).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  return (
    <div className="email-edit-body">
      <div className="task-edit-body__grid task-edit-body__grid--2col">
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">{t('email.direction')}</div>
          <EditableField
            value={email.direction}
            variant={{ kind: 'select', options: EMAIL_DIRECTIONS }}
            required
            onCommit={commitField('direction')}
            label={t('email.direction')}
          />
        </div>
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">{t('email.emailDate')}</div>
          <EditableField
            value={email.emailDate}
            variant={{ kind: 'datetime' }}
            required
            onCommit={commitField('emailDate')}
            label={t('email.emailDate')}
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field-row__label text-xs text-muted">{t('email.description')}</div>
        <RichTextField
          value={email.description}
          onCommit={commitField('description')}
          placeholder={t('email.descriptionPlaceholder')}
          required
        />
      </div>

      <ActivityTools
        {...recordsPickerProps}
        records={currentlyLinked}
        onChange={(val) => handleRecordsChange?.(val)}
        recordsSaving={recordsSaving}
        entityFqn={emailFqn ?? ''}
        entityId={email.emailId}
      />
    </div>
  );
}
